import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Newspaper, GitMerge, Wrench, Flame, Search, CheckCircle2, XCircle } from "lucide-react";

const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-xs font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm";
const box = "border border-[#2C2D35] rounded-sm";
const hd = "px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2";
const CMD = ["supervisor", "watch_commander", "records_supervisor", "admin"];
const PRI_COLOR = { 1: "#EF4444", 2: "#F59E0B", 3: "#3B82F6" };

function Briefing() {
  const [b, setB] = useState(null);
  useEffect(() => { client.get("/briefing").then((r) => setB(r.data)).catch(() => {}); }, []);
  if (!b) return <div className="font-mono text-[11px] text-[#6B7280] p-3">Compiling briefing…</div>;
  return (
    <div className="space-y-3" data-testid="briefing-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Calls (window)", b.total_calls], ["Still Open", b.still_open], ["Units OOS", b.units_out_of_service.length], ["Degraded Resources", b.degraded_resources.length]].map(([l, v]) => (
          <div key={l} className="bg-[#141518] border border-[#2C2D35] px-4 py-3 rounded-sm">
            <div className="font-mono text-[10px] text-[#9CA3AF] uppercase">{l}</div>
            <div className="font-mono font-bold text-2xl mt-1 text-white">{v}</div>
          </div>))}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className={box}>
          <div className={hd}>TOP CALL TYPES · LAST {b.hours}H</div>
          {b.top_types.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No calls in window.</div>}
          {b.top_types.map(([t, n]) => (
            <div key={t} className="flex px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[11px]"><span className="text-white">{t}</span><span className="ml-auto text-[#22D3EE]">{n}</span></div>))}
        </div>
        <div className={box}>
          <div className={hd}>REPEAT ADDRESSES (2+ CALLS)</div>
          {b.repeat_addresses.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No repeat addresses in window.</div>}
          {b.repeat_addresses.map((r) => (
            <div key={r.address} className="flex px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[11px]"><span className="text-white truncate">{r.address}</span><span className="ml-auto text-[#F59E0B]">×{r.count}</span></div>))}
        </div>
        <div className={box} data-testid="briefing-hot">
          <div className={hd}><Flame size={12} className="text-[#EA580C]" /> HOT REPEAT PLAYERS</div>
          {b.hot_persons.length === 0 && b.hot_vehicles.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No hot persons/vehicles right now.</div>}
          {b.hot_persons.map((p) => (
            <div key={p.label} className="flex px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[11px]"><span className="text-white">{p.label}</span><span className="ml-auto text-[#EA580C] font-bold">{p.recent} in 30d{p.spike ? " ▲" : ""}</span></div>))}
          {b.hot_vehicles.map((v) => (
            <div key={v.label} className="flex px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[11px]"><span className="text-[#22D3EE]">{v.label}</span><span className="text-[#9CA3AF] ml-2 truncate">{v.desc}</span><span className="ml-auto text-[#EA580C] font-bold">{v.recent} in 30d{v.spike ? " ▲" : ""}</span></div>))}
        </div>
        <div className={box}>
          <div className={hd}>OPEN CRITICAL (P1) · OOS UNITS · DEGRADED RESOURCES</div>
          {b.open_critical.map((i) => (
            <div key={i.cc_number} className="flex gap-2 px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[11px]"><span className="text-[#EF4444] font-bold">P1</span><span className="text-[#22D3EE]">{i.cc_number}</span><span className="text-white truncate">{i.call_type_label}</span></div>))}
          {b.units_out_of_service.map((u) => (
            <div key={u.unit_id} className="flex gap-2 px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[11px]"><span className="text-[#F59E0B]">OOS</span><span className="text-white">{u.unit_id}</span><span className="text-[#6B7280]">{u.agency}</span></div>))}
          {b.degraded_resources.map((r) => (
            <div key={r.id} className="flex gap-2 px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[11px]"><span className="text-[#EF4444] uppercase">{r.status}</span><span className="text-white">{r.name}</span></div>))}
          {b.open_critical.length + b.units_out_of_service.length + b.degraded_resources.length === 0 &&
            <div className="p-3 font-mono text-[11px] text-[#22C55E]">Nothing critical outstanding.</div>}
        </div>
      </div>
      <div className="font-mono text-[9px] text-[#6B7280]">Generated {b.generated_at.slice(0, 19).replace("T", " ")} UTC · window {b.hours}h</div>
    </div>
  );
}

function Deconfliction() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (q.trim().length < 3) { toast.error("Enter at least 3 characters"); return; }
    setBusy(true);
    try { const { data } = await client.get("/deconflict", { params: { address: q } }); setRes(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="deconflict-panel">
      <div className="flex gap-2">
        <input data-testid="deconflict-input" className={`${inp} w-96`} value={q} placeholder="Address to deconflict (e.g. 100 Main St)…"
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} />
        <button data-testid="deconflict-run" onClick={run} disabled={busy} className="flex items-center gap-1 px-3 py-2 text-[10px] font-head font-bold bg-[#22D3EE] text-black rounded-sm"><Search size={12} /> CHECK</button>
      </div>
      {res && (
        <>
          <div data-testid="deconflict-result" className="border px-4 py-3 rounded-sm font-mono text-xs"
            style={{ borderColor: res.conflict ? "#EF444466" : "#22C55E66", color: res.conflict ? "#EF4444" : "#22C55E" }}>
            {res.conflict
              ? `CONFLICT — ${res.active.length} active call(s) already at/near this location. Units committed: ${res.units_committed.join(", ") || "none"}`
              : `CLEAR — no active calls at this location. ${res.recent.length} closed call(s) in the last ${res.hours}h shown below.`}
          </div>
          {[["ACTIVE CALLS AT LOCATION", res.active, true], [`RECENT HISTORY (LAST ${res.hours}H)`, res.recent, false]].map(([title, rows, isActive]) => (
            <div key={title} className={box}>
              <div className={hd}>{title}</div>
              {rows.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">None.</div>}
              {rows.map((r) => (
                <div key={`${r.cc_number}${isActive}`} className="flex items-center gap-2 px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[11px] flex-wrap">
                  <span className="font-bold" style={{ color: PRI_COLOR[r.priority] || "#9CA3AF" }}>P{r.priority}</span>
                  <span className="text-[#22D3EE]">{r.cc_number}</span>
                  <span className="text-white">{r.type}</span>
                  <span className="text-[#6B7280] truncate">{r.address}</span>
                  {isActive && r.units.length > 0 && <span className="text-[#F59E0B]">units: {r.units.join(", ")}</span>}
                  <span className="ml-auto text-[#6B7280]">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>))}
            </div>))}
        </>
      )}
    </div>
  );
}

function Corrections() {
  const { user } = useAuth();
  const canDecide = CMD.includes(user?.role);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ kind: "incident_address", cc_number: "", current: "", proposed: "", reason: "" });
  const load = useCallback(() => client.get("/corrections").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await client.post("/corrections", form); toast.success("Correction submitted for review"); setForm({ kind: "incident_address", cc_number: "", current: "", proposed: "", reason: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };
  const decide = async (c, action) => {
    try { await client.post(`/corrections/${c.id}/${action}`); toast.success(action === "apply" ? "Applied to the record" : "Rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); }
  };
  const ST_COLOR = { pending: "#F59E0B", applied: "#22C55E", rejected: "#EF4444" };

  return (
    <div className="space-y-3" data-testid="corrections-panel">
      <div className={box}>
        <div className={hd}>SUBMIT CORRECTION (any user) — applied only after supervisor review, fully audited</div>
        <div className="p-3 flex flex-wrap gap-2 items-end">
          <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Kind</label>
            <select data-testid="corr-kind" className={inp} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {(data?.kinds || ["incident_address"]).map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
            </select></div>
          <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">CC Number</label><input data-testid="corr-cc" className={`${inp} w-32`} value={form.cc_number} placeholder="2026-12345" onChange={(e) => setForm({ ...form, cc_number: e.target.value })} /></div>
          {form.kind !== "incident_address" && <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Current value</label><input data-testid="corr-current" className={inp} value={form.current} placeholder="as recorded now" onChange={(e) => setForm({ ...form, current: e.target.value })} /></div>}
          <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Proposed value</label><input data-testid="corr-proposed" className={`${inp} w-56`} value={form.proposed} onChange={(e) => setForm({ ...form, proposed: e.target.value })} /></div>
          <div className="flex-1 min-w-[160px]"><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Reason</label><input data-testid="corr-reason" className={`${inp} w-full`} value={form.reason} placeholder="Why is this wrong?" onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
          <button data-testid="corr-submit" onClick={submit} className="px-3 py-2 text-[10px] font-head font-bold bg-[#22D3EE] text-black rounded-sm">SUBMIT</button>
        </div>
      </div>
      <div className={box}>
        <div className={hd}>CORRECTION QUEUE</div>
        {(data?.corrections || []).length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No corrections submitted yet.</div>}
        {(data?.corrections || []).map((c) => (
          <div key={c.id} className="flex items-center gap-2 px-3 py-2 border-b border-[#1E1F24] font-mono text-[11px] flex-wrap" data-testid={`corr-row-${c.id}`}>
            <span className="font-bold text-[9px] uppercase" style={{ color: ST_COLOR[c.status] }}>{c.status}</span>
            <span className="text-[#22D3EE]">{c.cc_number}</span>
            <span className="text-[#6B7280] uppercase text-[9px]">{c.kind.replace(/_/g, " ")}</span>
            <span className="text-[#EF4444] line-through truncate max-w-[180px]">{c.current || "—"}</span>
            <span className="text-[#22C55E] truncate max-w-[180px]">→ {c.proposed}</span>
            <span className="text-[#9CA3AF] truncate flex-1">{c.reason}</span>
            <span className="text-[#6B7280] text-[9px]">{c.by}</span>
            {c.status === "pending" && canDecide && <>
              <button data-testid={`corr-apply-${c.id}`} onClick={() => decide(c, "apply")} className="flex items-center gap-1 px-2 py-1 text-[9px] font-head font-bold bg-[#22C55E]/15 border border-[#22C55E]/50 text-[#4ade80] rounded-sm uppercase"><CheckCircle2 size={10} /> Apply</button>
              <button data-testid={`corr-reject-${c.id}`} onClick={() => decide(c, "reject")} className="flex items-center gap-1 px-2 py-1 text-[9px] font-head font-bold bg-[#EF4444]/15 border border-[#EF4444]/50 text-[#EF4444] rounded-sm uppercase"><XCircle size={10} /> Reject</button>
            </>}
          </div>))}
      </div>
    </div>
  );
}

const TABS = [
  { id: "briefing", label: "Shift Briefing", icon: Newspaper },
  { id: "deconflict", label: "Deconfliction", icon: GitMerge },
  { id: "corrections", label: "Data Corrections", icon: Wrench },
];

export default function ShiftIntel() {
  const [tab, setTab] = useState("briefing");
  return (
    <Layout>
      <PageHeader title="Shift Intelligence" sub="Oncoming-shift briefing · incident deconfliction · audited data corrections" icon={Newspaper} />
      <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="shiftintel-page">
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`shiftintel-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-head font-bold uppercase rounded-sm border ${tab === t.id ? "bg-[#22D3EE] text-black border-[#22D3EE]" : "border-[#2C2D35] text-[#9CA3AF]"}`}>
              <t.icon size={12} /> {t.label}
            </button>))}
        </div>
        {tab === "briefing" && <Briefing />}
        {tab === "deconflict" && <Deconfliction />}
        {tab === "corrections" && <Corrections />}
      </div>
    </Layout>
  );
}
